import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Passbook } from '@/shared/types';
import { ApiError } from '@/shared/api/client';
import { useAuth } from '@/shared/lib/auth';
import { useSurveys } from '@/features/nps/api/use-nps';
import {
  DEFAULT_SURVEY_BONUS_MESSAGE,
  DEFAULT_SURVEY_LINK_TITLE,
  DEFAULT_SURVEY_MESSAGE,
  SURVEY_ID_RE,
  SurveyCardFields,
  parseBonusQuantity,
  type SurveyCardValues,
  updateCouponSettings,
  updateSurveySettings,
  updateTierSettings,
  updateVarejoOnlineSettings,
  useVarejoOnlineSettings,
} from '@/features/settings';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import {
  permissionKeys,
  useUpdatePassbook,
  type PassbookFlagKey,
  type PassbookPermissionRow,
} from '../api/use-permissions';
import { PROGRAM_MODULE_FLAGS, type PageFlag } from '../model/page-flags';
import { FlagGrid, FlagRow, FlagSection } from './flag-table';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

/** Atualiza o passbook da loja e a linha de Permissões depois de salvar um recurso. */
function useRefreshPassbook(idPassbook: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['passbook', idPassbook] }),
      queryClient.invalidateQueries({ queryKey: permissionKeys.passbook(idPassbook) }),
    ]);
}

/** Switch que salva na hora: responde no clique e volta se o legado recusar. */
function useInstantToggle(value: boolean, save: (next: boolean) => Promise<void>) {
  const [draft, setDraft] = useState<boolean | null>(null);
  const toggle = async (next: boolean) => {
    setDraft(next);
    try {
      await save(next);
    } finally {
      setDraft(null);
    }
  };
  return { checked: draft ?? value, saving: draft !== null, toggle };
}

function TiersRow({ idPassbook, passbook }: { idPassbook: string; passbook: Passbook }) {
  const refresh = useRefreshPassbook(idPassbook);
  const { checked, saving, toggle } = useInstantToggle(Boolean(passbook.hasTiers), async (next) => {
    try {
      await updateTierSettings(idPassbook, { hasTiers: next });
      await refresh();
      toast.success(next ? 'Tiers ligados.' : 'Tiers desligados. Os níveis continuam salvos.');
    } catch (err) {
      toast.error(errorMessage(err, 'Não deu para alterar os tiers.'));
    }
  });
  return (
    <FlagRow
      id="module-tiers"
      label="Tiers de fidelidade"
      effect="Liga os níveis no cartão, na área do participante e nos leads. Desligar não apaga os níveis."
      checked={checked}
      disabled={saving}
      onCheckedChange={(next) => void toggle(next)}
    />
  );
}

/** Módulo que é flag do passbook (Produtos, Indique e ganhe…): salva na hora, otimista. */
function PassbookModuleRow({ row, flag }: { row: PassbookPermissionRow; flag: PageFlag }) {
  const queryClient = useQueryClient();
  const update = useUpdatePassbook(row.id);
  const toggle = (next: boolean) =>
    update.mutate(
      { flags: { [flag.key]: next } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: ['passbook', row.id] });
          toast.success(`${flag.label} ${next ? 'ligado' : 'desligado'}.`);
        },
        onError: () => toast.error(`Não deu para alterar ${flag.label}.`),
      },
    );
  return (
    <FlagRow
      id={`module-${flag.key}`}
      label={flag.label}
      effect={flag.effect}
      checked={row.flags[flag.key]}
      onCheckedChange={toggle}
    />
  );
}

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

function CouponsRow({ idPassbook, passbook }: { idPassbook: string; passbook: Passbook }) {
  const refresh = useRefreshPassbook(idPassbook);
  const savedOn = Boolean(passbook.hasCoupons);
  const [enabled, setEnabled] = useState(savedOn);
  const [couponName, setCouponName] = useState(passbook.couponName ?? '');
  const [couponStoreUrl, setCouponStoreUrl] = useState(passbook.couponStoreUrl ?? '');
  const [saving, setSaving] = useState(false);

  const url = couponStoreUrl.trim();
  const urlInvalid = url !== '' && !isHttpsUrl(url);

  const save = async (hasCoupons: boolean) => {
    setSaving(true);
    try {
      await updateCouponSettings(
        idPassbook,
        hasCoupons ? { hasCoupons, couponName: couponName.trim(), couponStoreUrl: url } : { hasCoupons },
      );
      await refresh();
      toast.success(hasCoupons ? 'Cupons salvos.' : 'Cupons desligados.');
    } catch (err) {
      setEnabled(savedOn);
      toast.error(errorMessage(err, 'Não deu para salvar os cupons.'));
    } finally {
      setSaving(false);
    }
  };

  // Desligar salva na hora; ligar abre os campos e só grava no "Salvar".
  const onToggle = (next: boolean) => {
    setEnabled(next);
    if (!next && savedOn) void save(false);
  };

  return (
    <FlagRow
      id="module-coupons"
      label="Cupons"
      effect="Mostra na área do cliente os códigos já atribuídos a ele. Ligar não cria cupom."
      checked={enabled}
      disabled={saving}
      onCheckedChange={onToggle}
      detail={
        enabled ? (
          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <div className="space-y-1.5">
              <Label htmlFor="couponName">Nome do cupom</Label>
              <Input
                id="couponName"
                value={couponName}
                onChange={(e) => setCouponName(e.target.value)}
                maxLength={60}
                placeholder="Ex.: Frete grátis"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="couponStoreUrl">Site da loja</Label>
              <Input
                id="couponStoreUrl"
                type="url"
                inputMode="url"
                value={couponStoreUrl}
                onChange={(e) => setCouponStoreUrl(e.target.value)}
                placeholder="https://www.sualoja.com.br"
                aria-invalid={urlInvalid || undefined}
              />
              {urlInvalid ? <p className="text-xs text-destructive">Precisa começar com https://</p> : null}
            </div>
            <Button size="sm" onClick={() => void save(true)} loading={saving} disabled={urlInvalid}>
              Salvar
            </Button>
          </div>
        ) : null
      }
    />
  );
}

function SurveyRow({ idPassbook, passbook }: { idPassbook: string; passbook: Passbook }) {
  const refresh = useRefreshPassbook(idPassbook);
  const { authSource } = useAuth();
  const { data: surveys = [], isError } = useSurveys({ enabled: authSource === 'v2' });
  const saved = passbook.regraCartao;
  const [enabled, setEnabled] = useState(Boolean(saved?.hasSurvey));
  const [surveyId, setSurveyId] = useState(saved?.idSurvey ?? '');
  const [card, setCard] = useState<SurveyCardValues>({
    messageSurvey: saved?.messageSurvey ?? DEFAULT_SURVEY_MESSAGE,
    titleLinkSurvey: saved?.titleLinkSurvey ?? DEFAULT_SURVEY_LINK_TITLE,
    mensagemBonificacaoNPS: saved?.mensagemBonificacaoNPS ?? DEFAULT_SURVEY_BONUS_MESSAGE,
    qtdeBonificarNPS: saved?.qtdeBonificarNPS ? String(saved.qtdeBonificarNPS) : '0',
  });
  const [saving, setSaving] = useState(false);

  const options = useMemo(
    () => [...surveys].sort((a, b) => Number(b.status === 'ACTIVE') - Number(a.status === 'ACTIVE')),
    [surveys],
  );
  const selectedInList = options.some((s) => s.id === surveyId);
  const canSave = SURVEY_ID_RE.test(surveyId.trim());

  const save = async (hasSurvey: boolean) => {
    setSaving(true);
    try {
      await updateSurveySettings(idPassbook, {
        hasSurvey,
        idSurvey: surveyId.trim() || undefined,
        messageSurvey: card.messageSurvey,
        titleLinkSurvey: card.titleLinkSurvey,
        mensagemBonificacaoNPS: card.mensagemBonificacaoNPS,
        qtdeBonificarNPS: parseBonusQuantity(card.qtdeBonificarNPS),
      });
      await refresh();
      toast.success(hasSurvey ? 'Pesquisa ligada.' : 'Pesquisa desligada.');
    } catch (err) {
      setEnabled(Boolean(saved?.hasSurvey));
      toast.error(errorMessage(err, 'Não deu para salvar a pesquisa.'));
    } finally {
      setSaving(false);
    }
  };

  const onToggle = (next: boolean) => {
    setEnabled(next);
    if (!next && saved?.hasSurvey) void save(false);
  };

  return (
    <FlagRow
      id="module-survey"
      label="Pesquisa de satisfação"
      effect="Vincula uma pesquisa de NPS à loja: área do cliente e link no e-mail transacional."
      checked={enabled}
      disabled={saving}
      onCheckedChange={onToggle}
      detail={
        enabled ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {options.length > 0 && !isError ? (
              <div className="space-y-1.5">
                <Label>Pesquisa</Label>
                <Select value={surveyId || undefined} onValueChange={setSurveyId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Escolha uma pesquisa" />
                  </SelectTrigger>
                  <SelectContent>
                    {surveyId && !selectedInList ? <SelectItem value={surveyId}>{surveyId}</SelectItem> : null}
                    {options.map((survey) => (
                      <SelectItem key={survey.id} value={survey.id}>
                        {survey.title}
                        {survey.status !== 'ACTIVE' ? ` (${survey.status})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="space-y-1.5">
              <Label htmlFor="idSurvey">Id da pesquisa</Label>
              <Input
                id="idSurvey"
                value={surveyId}
                onChange={(e) => setSurveyId(e.target.value)}
                placeholder="Cole o id se a lista não aparecer"
                className="font-mono text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <SurveyCardFields
                values={card}
                onChange={(patch) => setCard((prev) => ({ ...prev, ...patch }))}
                passbook={passbook}
                disabled={saving}
                idPrefix="module-survey"
              />
            </div>
            <div className="flex items-end justify-end">
              <Button size="sm" onClick={() => void save(true)} loading={saving} disabled={!canSave}>
                Salvar
              </Button>
            </div>
          </div>
        ) : null
      }
    />
  );
}

function VarejoOnlineRow({ idPassbook }: { idPassbook: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useVarejoOnlineSettings(idPassbook);
  const { checked, saving, toggle } = useInstantToggle(Boolean(data?.active), async (next) => {
    try {
      await updateVarejoOnlineSettings(idPassbook, next);
      await queryClient.invalidateQueries({ queryKey: ['varejonline-settings', idPassbook] });
      toast.success(next ? 'Envio ao Varejo Online ligado.' : 'Envio ao Varejo Online desligado.');
    } catch (err) {
      toast.error(errorMessage(err, 'Não deu para salvar o Varejo Online.'));
    }
  });

  let aside: string | undefined;
  if (isLoading) aside = 'carregando…';
  else if (isError) aside = 'sem configuração';
  else if (data) {
    const queue = `${data.pending} na fila · ${data.failed} ${data.failed === 1 ? 'falha' : 'falhas'}`;
    aside = !data.configured ? `${queue} · sem token neste ambiente` : !data.enabled ? `${queue} · fila pausada` : queue;
  }

  return (
    <FlagRow
      id="module-varejonline"
      label="Varejo Online"
      effect="Cadastro e alteração de cliente desta loja entram na fila do Varejo Online."
      checked={checked}
      disabled={saving || !data}
      onCheckedChange={(next) => void toggle(next)}
      aside={aside}
    />
  );
}

function moduleRows(row: PassbookPermissionRow, keys: PassbookFlagKey[]) {
  return keys
    .map((key) => PROGRAM_MODULE_FLAGS.find((flag) => flag.key === key))
    .filter((flag): flag is PageFlag => Boolean(flag))
    .map((flag) => <PassbookModuleRow key={flag.key} row={row} flag={flag} />);
}

/** Recursos do programa da loja, em tabela. Quem vê cada página fica em Páginas. */
export function ModulesTable({
  idPassbook,
  passbook,
  row,
}: {
  idPassbook: string;
  passbook: Passbook;
  row: PassbookPermissionRow;
}) {
  return (
    <FlagGrid>
      <FlagSection title="Canais de campanha">
        {moduleRows(row, ['hasCampaignAdvanced', 'hasEmailMarketing', 'hasSmsMarketing', 'hasCampaign', 'hasCampaignMetabase', 'hasSimpleCampaignWhatsApp'])}
      </FlagSection>
      <FlagSection title="Fidelidade">
        <TiersRow idPassbook={idPassbook} passbook={passbook} />
        {moduleRows(row, ['hasLoyaltyCampaigns', 'hasMemberGetMember'])}
      </FlagSection>
      <FlagSection title="Público e operação">
        {moduleRows(row, ['hasSegmentations', 'hasMultiStore'])}
      </FlagSection>
      <FlagSection title="Catálogo e parceiros">{moduleRows(row, ['hasProducts', 'hasInfluencerCard'])}</FlagSection>
      <FlagSection title="Integrações">
        <VarejoOnlineRow idPassbook={idPassbook} />
      </FlagSection>
      <FlagSection title="Área do participante">
        <CouponsRow key={`coupons-${idPassbook}`} idPassbook={idPassbook} passbook={passbook} />
        <SurveyRow key={`survey-${idPassbook}`} idPassbook={idPassbook} passbook={passbook} />
      </FlagSection>
    </FlagGrid>
  );
}
